<script setup lang="ts">
import {
    AttackSkill,
    Skill,
    PhysicalAttackSkill,
    SpecialAttackSkill,
    StatusSkill,
} from "../core/skill";

const props = defineProps<{ skill: Skill }>();

function getSkillKind(skill: Skill): string | undefined {
    if (skill instanceof PhysicalAttackSkill) return "物理攻击";
    if (skill instanceof SpecialAttackSkill) return "特殊攻击";
    if (skill instanceof StatusSkill) return "属性攻击";
    return undefined;
}

function getTypeColor(skill: Skill): string {
    if (skill instanceof PhysicalAttackSkill) return "#ef4444";
    if (skill instanceof SpecialAttackSkill) return "#ec4899";
    if (skill instanceof StatusSkill) return "#22c55e";
    return "#a0a0b0";
}

function getPowerLabel(skill: Skill): number | string {
    return skill instanceof AttackSkill && skill.power > 0 ? skill.power : "--";
}
</script>

<template>
    <div class="skill-info">
        <p class="title">
            <span class="name">{{ skill.name }}</span>
            <span
                class="skill-type-tag"
                :style="{ color: getTypeColor(skill), borderColor: getTypeColor(skill) + '44' }"
                >{{ getSkillKind(skill) }}</span
            >
        </p>
        <p class="meta">
            <span class="power"
                >威力：{{ getPowerLabel(skill) }}</span
            >
        </p>
        <p class="meta" v-if="skill.preemptiveLevel > 0">
            先制+{{ skill.preemptiveLevel }}
        </p>
        <p class="meta" v-else-if="skill.preemptiveLevel < 0">
            先制{{ skill.preemptiveLevel }}
        </p>
        <p class="meta" v-if="skill.sureHit">必中</p>
        <div class="desc-divider"></div>
        <p
            class="description"
            v-for="desc in skill.description"
            :key="desc"
            v-html="desc"
        ></p>
    </div>
</template>

<style scoped>
.title {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 8);
    padding: calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 10) 0 calc(var(--ui-unit) * 10);
    margin: 0;
}

.name {
    font-weight: 700;
    font-size: calc(var(--ui-unit) * 15);
    color: #ffd700;
    text-shadow: 0 0 calc(var(--ui-unit) * 8) rgba(255, 215, 0, 0.3);
}

.skill-type-tag {
    font-size: calc(var(--ui-unit) * 11);
    font-weight: 600;
    padding: calc(var(--ui-unit) * 1) calc(var(--ui-unit) * 8);
    border: calc(var(--ui-unit) * 1) solid;
    border-radius: calc(var(--ui-unit) * 10);
}

.meta {
    font-size: calc(var(--ui-unit) * 13);
    margin: 0;
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 10);
    color: #c0c0d0;
}

.power {
    color: #f59e0b;
    font-weight: 600;
}

.desc-divider {
    height: calc(var(--ui-unit) * 1);
    background: rgba(255, 255, 255, 0.06);
    margin: calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 10);
}

.description {
    font-size: calc(var(--ui-unit) * 12);
    margin: 0;
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 10);
    padding-bottom: calc(var(--ui-unit) * 4);
    max-width: calc(var(--ui-unit) * 240);
    height: auto;
    text-align: left;
    display: block;
    color: #b0b0c0;
    line-height: 1.5;
}

.description:last-child {
    padding-bottom: calc(var(--ui-unit) * 8);
}
</style>
